import posthog from "posthog-js"

/**
 * Initialize PostHog for product analytics and user identification
 *
 * @see https://posthog.com/docs/libraries/js
 */
export function initPostHog() {
  const key = import.meta.env.VITE_POSTHOG_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE

  // Skip in test environment — no actual PostHog calls
  if (import.meta.env.MODE === "test") {
    return
  }

  // Skip initialization if key is not configured (local development)
  if (!key) {
    return
  }

  const apiHost = host || "https://us.i.posthog.com"
  // In development, skip /flags/ and remote config so invalid or placeholder keys don't cause 401/404
  const isDev = import.meta.env.DEV

  posthog.init(key, {
    api_host: apiHost,
    person_profiles: "identified_only",
    capture_pageview: true,
    // Align with Sentry environment for filtering in PostHog
    environment,
    // Prevent loading /array/{token}/config.js as script (avoids 404 + MIME error when CDN returns JSON)
    disable_external_dependency_loading: true,
    // In dev, skip feature flags and remote config to avoid 401/404 when key is unset or invalid
    ...(isDev && { advanced_disable_flags: true }),
  } as Parameters<typeof posthog.init>[1])
}

/**
 * Identify the current user for analytics
 */
export function identifyUser(
  id: string,
  properties?: { email?: string; [key: string]: unknown }
) {
  if (import.meta.env.MODE === "test") return
  posthog.identify(id)
  if (properties && Object.keys(properties).length > 0) {
    posthog.people.set(properties)
  }
}

/**
 * Clear user identity on logout
 */
export function resetUser() {
  if (import.meta.env.MODE === "test") return
  posthog.reset()
}

/**
 * Track a custom event with optional properties
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>
) {
  if (import.meta.env.MODE === "test") return
  posthog.capture(name, properties)
}
